import React, { useEffect, useState } from "react";
import { Tag, Tooltip, Button } from "antd";
import Alert from "../../components/GxAlert";
import { FileSearchOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { scGetParties, scWarehouseInventory } from "../../api/client";
import { byStr, byNum, byDate } from "../../utils/sorters";
import { toJalali } from "../../utils/dateUtils";
import { fmtKg, fmtRial, CATEGORY_LABEL } from "./scConstants";
import GxTable from "../../components/GxTable";
import { faNum } from "../../utils/gxSummary";

// جدولِ موجودیِ انبار قرچک با «فی تمام‌شدهٔ زنجیره» — هم در تب گزارش‌ها هم پایین داشبورد.
// اگر partyId ندهید، خودش انبار داخلی را پیدا می‌کند.
// موجودی «محاسباتی» است و مستقیم ویرایش نمی‌شود؛ اصلاح از طریقِ اسنادِ منبع (رویدادها) انجام می‌شود.
export default function WarehouseInventoryTable({ partyId, compact = false }) {
  const [inv, setInv] = useState(null);
  const [pid, setPid] = useState(partyId || null);
  const navigate = useNavigate();

  useEffect(() => {
    const load = (p) => { setPid(p); scWarehouseInventory(p).then((r) => setInv(r.data)).catch(() => {}); };
    if (partyId) { load(partyId); return; }
    scGetParties({ party_type: "internal_warehouse" })
      .then((r) => { if (r.data?.length) load(r.data[0].id); })
      .catch(() => {});
  }, [partyId]);

  const goToSourceEvents = () => navigate(`/supply-chain/events?party_id=${pid || ""}`);

  const openInvoice = (r) => {
    if (r?.event_id) navigate(`/supply-chain/events/${r.event_id}`);
    else goToSourceEvents();
  };

  const cols = [
    { title: "تاریخ فاکتور", dataIndex: "event_date", width: 110, sorter: byDate("event_date"),
      gxFilter: "dateRange",
      render: (v) => (v ? toJalali(v) : "—") },
    { title: "شماره فاکتور", dataIndex: "external_invoice_number", width: 120, sorter: byStr("external_invoice_number"),
      gxFilter: "text",
      render: (v) => v || "—" },
    { title: "کالا", dataIndex: "item_category", width: 110, ellipsis: true, sorter: byStr("item_category"),
      gxFilter: "enum",
      gxFilterOptions: Object.entries(CATEGORY_LABEL).map(([v, l]) => ({ text: l, value: v })),
      render: (v) => <Tag>{CATEGORY_LABEL[v] || v}</Tag> },
    { title: "نوع پارچه", dataIndex: "fabric_type", width: 130, ellipsis: true, sorter: byStr("fabric_type"),
      gxFilter: "enum", render: (v) => v || "—" },
    { title: "رنگ", dataIndex: "color", width: 90, ellipsis: true, sorter: byStr("color"),
      gxFilter: "enum", render: (v) => v || "—" },
    { title: "موجودی", dataIndex: "balance_kg", width: 110, align: "center", sorter: byNum("balance_kg"),
      defaultSortOrder: "descend", gxFilter: "num",
      render: (v) => <b className="gx-num">{fmtKg(v)}</b> },
    { title: "طاقه", dataIndex: "roll_count", width: 70, align: "center", sorter: byNum("roll_count"),
      gxFilter: "num", render: (v) => v ?? "—" },
    { title: "فی آخرین مرحله", dataIndex: "unit_price", width: 120, align: "center", sorter: byNum("unit_price"),
      gxFilter: "num",
      render: (v) => <span className="gx-num">{fmtRial(v)}</span> },
    { title: "فی تمام‌شده (زنجیره)", dataIndex: "full_cost_per_kg", width: 150, align: "center", sorter: byNum("full_cost_per_kg"),
      gxFilter: "num",
      render: (v, r) => v == null ? "—" : (
        <div className="gx-cost-cell">
          <b className="gx-num" style={{ color: "#107C41" }}>{fmtRial(v)}</b>
          {r.full_cost_estimated && <Tooltip title="از میانگینِ کلِ زنجیره برآورد شده">
            <Tag color="orange" style={{ margin: 0 }}>تقریبی</Tag></Tooltip>}
        </div>
      ) },
    { title: "ارزش تمام‌شده", dataIndex: "full_total_value", width: 130, align: "center", sorter: byNum("full_total_value"),
      gxFilter: "num",
      render: (v) => <span className="gx-num">{fmtRial(v)}</span> },
  ];

  const invSummary = [
    { dataIndex: "item_category", type: "unique", labelOf: (v) => CATEGORY_LABEL[v] || v },
    { dataIndex: "fabric_type", type: "unique" },
    { dataIndex: "color", type: "unique" },
    { dataIndex: "balance_kg", type: "sum", format: (v) => fmtKg(v) },
    { dataIndex: "roll_count", type: "sum", format: (v) => faNum(v) },
    { dataIndex: "unit_price", type: "wavg", weightKey: "balance_kg", format: (v) => fmtRial(v) },
    { dataIndex: "full_cost_per_kg", type: "wavg", weightKey: "balance_kg", format: (v) => fmtRial(v) },
    { dataIndex: "full_total_value", type: "sum", format: (v) => fmtRial(v) },
  ];

  return (
    <div>
      {!compact && (
        <Alert type="info" showIcon style={{ marginBottom: 8 }}
          message="روی هر ردیف کلیک کنید تا فاکتور منبع باز شود. موجودی مستقیم ویرایش نمی‌شود."
          action={
            <Button size="small" icon={<FileSearchOutlined />} onClick={goToSourceEvents}>
              همهٔ اسناد منبع
            </Button>
          } />
      )}
      <GxTable dataSource={inv?.rows || []} columns={cols} size="small"
        layoutId="sc-warehouse-inv"
        rowKey={(r) => `${r.item_category}-${r.fabric_type || ""}-${r.color || ""}-${r.event_id || ""}`}
        pagination={!compact && (inv?.rows || []).length > 10 ? { pageSize: 10 } : false}
        scroll={{ x: 1100 }} tableLayout="fixed"
        gxSummary={invSummary}
        onRow={(r) => ({
          onClick: () => openInvoice(r),
          style: { cursor: r.event_id ? "pointer" : "default" },
        })} />
    </div>
  );
}
